/**
 * @file locale-context.ts
 *
 * The admin-UI locale context and its `useLocale` hook, split from
 * {@link LocaleProvider} so components can depend on the hook without importing
 * the provider component (and so the hook is unit-testable in isolation).
 */

import { createContext, useContext } from 'react';
import type { SupportedLocale } from '@wsb/i18n';

/** localStorage key under which the admin's chosen UI locale is persisted. */
export const ADMIN_LOCALE_STORAGE_KEY = 'wsb.adminLocale';

/** Active admin-UI locale and the setter that switches it at runtime. */
export interface LocaleContextValue {
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
}

export const LocaleContext = createContext<LocaleContextValue | undefined>(
  undefined,
);

/**
 * Accesses the active admin-UI locale and its setter.
 *
 * @returns The current {@link LocaleContextValue}.
 *
 * @throws {Error} When used outside a {@link LocaleProvider}.
 */
export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
}
